/* eslint-disable indent */
import loadable from '@loadable/component'
import * as DOMPurify from 'dompurify'
import moment from 'moment'
import { isArray } from 'lodash'
import { resolveCurrencyCode } from 'Schemas/currencies'
import { routesList } from 'Schemas/routesList'
import { DEFAULT_BANK, DEFAULT_LANG, ELIGIBLE_ROLES, SUPPORTED_LANGUAGES, ROUTES_AND_PERMISSIONS, RSQL_OPERATOR_MAP, TENANT_BY_THEME, PROVIDERID_FOR_THEME } from 'Schemas/constants'
import { getBankFromPath } from 'Utility/bankMethods'

export const getFaviconPath = (theme) => {
  const faviconName = theme === 'uci' ? 'favicon-uci.ico' : 'favicon.ico'
  return `${process.env.PUBLIC_URL || ''}/${faviconName}`
}

export const setFaviconForTheme = (theme) => {
  const faviconLink = document.querySelector('link[rel~="icon"]') || document.createElement('link')
  faviconLink.rel = 'icon'
  faviconLink.href = getFaviconPath(theme)
  if (!faviconLink.parentNode) {
    document.head.appendChild(faviconLink)
  }
}


export const getLanguage = () => {
  const storageLanguage = window.localStorage.getItem('userLangBO')
  const browserLanguage = navigator.language.split('-')[0] // Takes only the main part of the language code (e.g. ‘en’ from ‘en-US’)

  if (SUPPORTED_LANGUAGES.includes(storageLanguage)) {
    return storageLanguage
  }

  if (storageLanguage === 'AutoDetect' || !storageLanguage) {    
    return SUPPORTED_LANGUAGES.includes(browserLanguage) ? browserLanguage : DEFAULT_LANG
  }
  return DEFAULT_LANG
}

export const getLanguageCalendar = () => {
  const lang = getLanguage()  
  return lang === 'en' ? 'en-GB' : lang
}

export const redirectToLogin = () => {
  window.sessionStorage.clear()
  window.location = buildLoginUrl()
}

export const buildLoginUrl = () => {
  const loginUrl = new URL(routesList.goToLogin, window.location.origin)
  const bankFromPath = getBankFromPath()
  const tenant = TENANT_BY_THEME[bankFromPath] || DEFAULT_BANK
  const providerId = PROVIDERID_FOR_THEME[tenant] || 'nb'

  loginUrl.searchParams.set('providerId', providerId)
  loginUrl.searchParams.set('tenant', tenant)
  return loginUrl.toString()
}

/**
 * Logs the user out by clearing the session storage and redirecting to the logout URL.
 */
export const logoutRedirect = (isSSO) => {
  const origin = window.location.origin
  const logoutUrl = new URL(process.env.LOGOUT, origin)

  const postLogoutRedirectUrl = isSSO
    ? `${origin}${routesList.goToExpiredSession(getBankFromPath())}`
    : buildLoginUrl()
  logoutUrl.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUrl)

  window.sessionStorage.clear()
  window.location = logoutUrl.toString()
}

/** @function
 * * Handling the API Rest resposnse, and return data and different message for status code
 * @name handlingResponse
 * @param {object} data - The response of method
 * @return {object} - The response of method, with additional info
 */
export const handlingResponse = (data) => {
  const response = { error: '' }
  if (data && data.status !== 200 && data.status !== 204) {
    switch (data.status) {
      case 400:
        data.type = 'Bad Request'
        break
      case 401:
        data.type = 'Unauthorized'
        break
      case 403:
        data.type = 'Forbidden'
        break
      case 404:
        data.type = 'Not Found'
        break
      case 405:
        data.type = 'Method Not Allowed'
        break
      case 409:
        data.type = 'Conflict'
        break
      case 500:
        data.type = 'ServerError'
        break
      default:
        data.type = 'Invalid Data'
        break
    }
    return { response, error: data }
  }
  return { response: data }
}

/**  @function
 * * Handling the null case and set to safe
 *
 * @name nullSafe
 * @param {function} func
 * @param {*} fallbackValue
 * @returns {*} the value to set safe the method
 */
export const nullSafe = (func, fallbackValue) => {
  try {
    const value = func()
    return value === null || value === undefined ? fallbackValue : value
  } catch (e) {
    return fallbackValue
  }
}

/**  @function
 * * Import a component with loadable library
 *
 * @name loadable
 * @returns {$Symbols} the required component
 */
// export const loadableComponent = (component) =>
//   loadable(() => import(
//     /* webpackChunkName: "[request]" */
//     /* webpackExclude: /\.test\.(js|jsx)$/ */
//     `Components/${component}`), {
//     fallback: <div> Loading... </div>
//   });

/**  @function
 * * Import a page with loadable library
 *
 * @name loadable
 * @returns {$Symbols} the required library
 */
export const LoadablePage = loadable(
  (props) =>
    import(
      /* webpackChunkName: "[request]" */
      /* webpackExclude: /\.test\.(js|jsx)$|\.md$/ */
      `Route/${props.page}/components/${props.page}Container`
    ),
  {
    cacheKey: (props) => props.page
  }
)

export const LoadableSubPage = loadable(
  (props) =>
    import(
      /* webpackChunkName: "[request]" */
      /* webpackExclude: /\.test\.(js|jsx)$|\.md$/ */
      `Route/${props.father}/components/${props.page}/${props.page}Container`
    ),
  {
    cacheKey: (props) => props.page
  }
)

export const LoadableComponentPage = loadable(
  (props) =>
    import(
      /* webpackChunkName: "[request]" */
      /* webpackExclude: /\.test\.(js|jsx)$|\.md$/ */
      `Components/${props.page}`
    ),
  {
    cacheKey: (props) => props.page
  }
)

/**  @function
 * * Retrieve the desired value, from an object
 *
 * @name getNestedObject
 * @param {object} nestedObj
 * @param {array} pathArr
 * @returns {*} the value from the chhosed key
 */

export const getNestedObject = (nestedObj, pathArr) =>
  pathArr.reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), nestedObj)

/**
 ** Sanitize the input and return it
 * @param {any} params
 * @returns {any}
 */

export const sanitizeInput = (params) => {
  let objectSanitized = Object.assign({})
  switch (typeof params) {
    case 'object':
      if (Array.isArray(params)) {
        objectSanitized = []
        params.forEach((v) => {
          if (typeof v === 'object') {
            const obj = {}
            for (const a in v) {
              obj[a] = DOMPurify.sanitize(v[a])
            }
            objectSanitized.push(obj)
          } else {
            objectSanitized.push(DOMPurify.sanitize(v))
          }
        })
      } else {
        for (const a in params) {
          if (Array.isArray(params[a])) {
            const obj = []
            params[a].forEach((v) => {
              obj.push(DOMPurify.sanitize(v))
            })
            objectSanitized[a] = obj
          } else {
            objectSanitized[a] = DOMPurify.sanitize(params[a])
          }
        }
      }
      return objectSanitized

    default:
      return DOMPurify.sanitize(params)
  }
}

/**
 * Truncates a string if it exceeds the specified maximum length.
 * @param {string} str - The string to truncate.
 * @param {number} maxLength - The maximum length of the string.
 * @returns {string} - The truncated string.
 */
export function truncateString(str, maxLength) {
  //TODO: Check this function
  const ellipsis = '...'
  if (!str) {
    return ''
  }
  if (str.length > maxLength) {
    return str.substring(0, maxLength - ellipsis.length) + ellipsis
  }
  return str
}

/**
 * Opens a URL in a new tab or the current tab.
 * @param {string} url - The URL to open.
 * @param {boolean} NewTab - Whether to open the URL in a new tab or the current tab.
 */
export const openUrl = (url, NewTab) => {
  //TODO: Check this function
  if (NewTab) {
    window.open(url, '_blank', 'noreferrer')
  } else {
    window.open(url, 'noreferrer')
  }
}

/**  @function
 * * encode Query String
 *
 * @name encodeQueryString
 * @returns {object}
 */
export const encodeQueryString = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return ''

  const query = new URLSearchParams(
    Object.entries(data).flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.filter((item) => item != null).map((item) => [key, String(item)])
        : value != null
          ? [[key, String(value)]]
          : []
    )
  ).toString()

  return query ? `?${query}` : ''
}

/**  @function
 * * removes empty values and empty objects in nested objects
 *
 * @name deleteEmptyValues
 * @returns {object}
 */
export const deleteEmptyValues = (obj) => {
  // Check whether the object is of type Date or Array and return it directly
  if (obj instanceof Date || Array.isArray(obj)) return obj
  return Object.entries(obj).reduce((newObj, [key, value]) => {
    // If the value is an object, execute the function recursively
    if (value && typeof value === 'object') {
      const cleanedValue = deleteEmptyValues(value)
      // If the object is not empty after cleaning, add it to newObj
      if (Object.keys(cleanedValue).length > 0 || Array.isArray(value)) newObj[key] = cleanedValue
    } else if (value != null && value !== '') {
      // If the value is not null or an empty string, add it to newObj
      newObj[key] = value
    }
    return newObj
  }, {})
}

/**
 * generates a unique serial number
 * @name generateUniqSerial
 * @returns {string} a serial number.
 */
export const generateUniqSerial = () =>
  'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, () => {
    const r = Math.floor(Math.random() * 16)
    return r.toString(16)
  })

/**
 * Generates an 18-character ID from a truncated prefix and a random alphanumeric suffix.
 * @name generateId
 * @param {string} [prefix=''] - The value prepended to the generated ID, truncated to 15 characters.
 * @returns {string} The 18-character generated ID.
 */
export const generateId = (prefix = '') => {
  const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const truncatedPrefix = prefix.slice(0, 15)
  const suffix = Array.from(
    { length: 18 - truncatedPrefix.length },
    () => characters[Math.floor(Math.random() * characters.length)]
  ).join('')

  return `${truncatedPrefix}${suffix}`
}

/**
 * maps roles from a User object and returns a semicolon-separated list
 * @name userRolesString
 * @returns {string} the role list.
 */
export const userRolesString = (list) => {
  if (!list || list.length === 0) return ''
  return list.map((role) => role.name).join('; ')
}

/**
 * enables file download by appending a download link to the document
 * @name exportDownload
 * @param {string|Blob} data - the file content or an existing file Blob
 * @param {string} name - the file name
 * @param {string} type - the file type
 */
export const exportDownload = (data = '', name = 'MyExport.csv', type = 'text/csv;charset=utf-8') => {
  // Keep an existing Blob's MIME type; wrap string responses using the requested type.
  const blob = data instanceof Blob ? data : new Blob([data], { type })
  const URL = window.URL || window.webkitURL
  const dataUrl = URL.createObjectURL(blob)
  // To download directly csv
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = name // Set filName

  document.body.append(link)
  link.click()
  link.remove()
}

/**
 * checks if the user has a specific permission against a UserPermission collection
 * @name userPermissionControl
 * @param {string} permission - the matched permission
 * @param {object} userPermissions - the user permission collection
 * @returns {boolean} the test result.
 */
export const userPermissionControl = (permission, userPermissions) => {
  if (!userPermissions || userPermissions.length === 0) return false

  return userPermissions.some((userPermission) => userPermission.key === permission)
}

/**
 * Filters the provided routes based on the user's permissions and profile information.
 *
 * @param {Array<Object>} routes - The list of route objects to filter. Each route should have a `key` property.
 * @param {Array<string>} userPermissions - The list of permissions assigned to the user.
 * @param {Object} userProfileInfo - The user's profile information, which may include `userProfile` and `getSubscriptionArray`.
 * @param {Object} userProfileInfo.userProfile - The user's profile details used for profile-based checks.
 * @param {Array<string>} userProfileInfo.getSubscriptionArray - The user's subscription array for additional checks.
 * @returns {Array<Object>} The filtered list of routes that the user is permitted to access.
 */
export const permittedRoutes = (routes, userPermissions, userProfileInfo) => {
  if (
    !Array.isArray(userPermissions) ||
    userPermissions.length === 0 ||
    !userProfileInfo ||
    !userProfileInfo.getSubscriptionArray ||
    !userProfileInfo.userProfile
  )
    // Allow Dashboard and Profile when user info is not available
    return routes.filter(({ key }) => key === 'Dashboard' || key === 'Profile')

  const hasMotoProfile = checkUserProfile(userProfileInfo.userProfile, 'MOTO')
  // const hasDisputeless = checkGetSubscription(userProfileInfo.getSubscriptionArray, 'DISPUTELESS')

  return routes.filter(({ key }) => {
    if (!key) return true
    if (key === 'Dashboard' || key === 'Profile') return true
    if (key === 'CardCheck') return hasMotoProfile
    if (key === 'Moto') return hasMotoProfile && userPermissionControl(ROUTES_AND_PERMISSIONS[key], userPermissions)
    return userPermissionControl(ROUTES_AND_PERMISSIONS[key], userPermissions)
  })
}

/**
 * finds the first elegible role for the user
 * @name userMainRoles
 * @param {string} roleList
 * @returns {string} the matched elegible role
 */
export const userMainRoles = (roleList) => {
  if (!roleList?.length) return ''
  const mainRole = roleList.map((role) => role.name).find((role) => ELIGIBLE_ROLES.includes(role))
  return mainRole || ''
}

/**
 * generate a list of options for the terminal id selects
 * @name terminalIdOptions
 * @returns {array} a list of terminal id options
 */
export const terminalIdOptions = (terminalIdList, allIdsOptionLabel = 'ALL') => {
  if (terminalIdList?.length > 1 && Array.isArray(terminalIdList)) {
    return [{ value: 'All', label: allIdsOptionLabel }].concat(
      terminalIdList.map((obj) => ({ value: obj.terminalId, label: obj.terminalId }))
    )
  } else if (terminalIdList?.length === 1 && Array.isArray(terminalIdList)) {
    return [{ value: terminalIdList[0].terminalId, label: terminalIdList[0].terminalId }]
  }
  return []
}

/**  @function
 
 * get query params
 * @name getQueryParams
 * @returns {object}
*/
export const getQueryParams = (symbol = '?') => {
  const queryParams = window.location.href.split(symbol)[1]
  if (queryParams) {
    return queryParams.split('&').reduce((obj, param) => {
      const [key, value] = param.split('=')
      obj[key] = value
      return obj
    }, {})
  }
  return {}
}

/**  @function
 
*url Encoded
*
*@name urlEncoded
*@returns {object}
*/
export const urlEncoded = (obj) =>
  Object.keys(obj)
    .map((key) => `${key}=${encodeURIComponent(obj[key])}`)
    .join('&')

export const updateTTL = () => {
  const date = moment().format('DD')
  const time = moment().add(5, 'minutes').format('HH:mm:ss')
  const obj = {
    DAY: date,
    TTL: time
  }
  window.sessionStorage.setItem('TTL', JSON.stringify(obj))
}

/**
 * Retrieves the name of a currency based on its code.
 *
 * @param {string|number} currency - The currency code, which can be a string or a number.
 * @returns {string} The name of the currency. If the currency code is invalid, returns the code itself.
 */
export const getCurrencyName = (currency) => {
  if (!currency) return ''
  const currencyCode = resolveCurrencyCode(currency)
  try {
    const name = new Intl.NumberFormat(getLanguage(), {
      style: 'currency',
      currency: currencyCode,
      maximumSignificantDigits: 1,
      currencyDisplay: 'name'
    }).format(1)
    const currencyName = name.replace('1', '').trim()
    return currencyName.charAt(0).toUpperCase() + currencyName.slice(1)
  } catch (e) {
    return currencyCode
  }
}

/**  @function
 * * luhnCheck
 *
 * @name luhnCheck
 * @returns {object}
 */
export const luhnCheck = (val) => {
  let checksum = 0 // running checksum total
  let j = 1 // takes value of 1 or 2

  if (val && val !== '') {
    // Process each digit one by one starting from the last
    // eslint-disable-next-line no-plusplus
    for (let i = val.length - 1; i >= 0; i--) {
      let calc = 0
      // Extract the next digit and multiply by 1 or 2 on alternative digits.
      calc = Number(val.charAt(i)) * j

      // If the result is in two digits add 1 to the checksum total
      if (calc > 9) {
        checksum += 1
        calc -= 10
      }

      // Add the units element to the checksum total
      checksum += calc
      // Switch the value of j
      if (j === 1) {
        j = 2
      } else {
        j = 1
      }
    }
  }
  // Check if it is divisible by 10 or not.
  return checksum % 10 === 0
}
/**
 * finds the provided css variable by name in the computed style and returns the matching value
 * @name getCssVariable
 * @returns {null}
 */
export const getCssVariable = (name) => {
  if (!name || !document) {
    return null
  }
  var style = getComputedStyle(document.body)
  return style.getPropertyValue(name)
}

/**
 * Retrieves the maximum number of installments for the GIN service from the selected terminal.
 *
 * @param {string} selectedTerminal - The ID of the selected terminal.
 * @param {Array} terminalIdList - The list of terminal objects.
 * @param {string} terminalIdList[].terminalId - The ID of the terminal.
 * @param {Array} terminalIdList[].vas - The list of value-added services (VAS) for the terminal.
 * @param {string} terminalIdList[].vas[].name - The name of the VAS.
 * @param {Array} terminalIdList[].vas[].params - The list of parameters for the VAS.
 * @param {string} terminalIdList[].vas[].params[].name - The name of the parameter.
 * @param {number} terminalIdList[].vas[].params[].value - The value of the parameter.
 * @returns {number|boolean} The maximum number of installments if found, otherwise false.
 */
export const getGinMaxInstallments = (selectedTerminal, terminalIdList) => {
  if (!selectedTerminal) return false

  const terminal = terminalIdList.find((t) => t.terminalId === selectedTerminal)
  const ginVas = terminal?.vas?.find((vas) => vas.name === 'GIN')
  const maxInstallmentsParam = ginVas?.params?.find((param) => param.name === 'maxInstallments' && param.value > 0)

  return maxInstallmentsParam?.value || false
}

/**
 * Detects the VAS (Value Added Services) case for a given terminal ID from a list of terminal IDs.
 *
 * @param {string} terminalId - The terminal ID to check.
 * @param {Array} terminalIdList - The list of terminal IDs with their associated VAS.
 * @returns {string|boolean} - Returns 'PLN_AND_GIN' if both PLN and GIN are present, 'PLN' if only PLN is present,
 *                             'GIN' if only GIN is present, and false if neither are present or if the input is invalid.
 */
export const vasCaseDetector = (terminalId, terminalIdList) => {
  if (!terminalId || !terminalIdList?.length) return false

  const temp = terminalIdList.find((t) => t.terminalId === terminalId)
  if (!temp?.vas?.length) return false

  const vasNames = temp.vas.map((vas) => vas?.name)
  const hasPLN = vasNames.includes('PLN')
  const hasGIN = vasNames.includes('GIN')

  if (hasPLN && hasGIN) return 'PLN_AND_GIN'
  if (hasPLN) return 'PLN'
  if (hasGIN) return 'GIN'

  return false
}

export const errorMessageManager = (error) => {
  switch (error) {
    case 'PS0002':
    case 'PS0022':
    case 'PS0023':
    case 'PS0024':
    case 'PS0027':
    case 'PS0028':
    case 'PS0079':
    case 'PS0080':
      return 'PS0022' // "Attention!The operation is not allowed."

    case 'PS0026':
    case 'PS0029':
    case 'PS0046':
    case 'PS0065':
    case 'PS0069':
    case 'PS0082':
      return 'PS0026' // "Your entries are not valid. Please check your entries."

    case 'PS0052':
      return 'PS0052' // "Attention! TerminalId not found in the system. Please verify."

    case 'PS0054':
      return 'PS0054' // "Date inserted is not  valid."

    default:
      return 'default' // "Something went wrong. Please try again later."
  }
}

/**
 * Checks if the action for a specific permission key is read-only
 * @param {string} permission - The the permission key to check
 * @param {object[]} userPermissions - The list of user permissions
 * @returns {boolean} - True if the key is read-only, false otherwise
 */
export const readOnlyControl = (permission, userPermissions) => {
  if (!userPermissions || userPermissions.length === 0) return true

  return !userPermissions.some((item) => item.key === permission && item.action === 'W')
}

/**
 * Checks whether the given SVG string is a valid SVG or not. Returns true if it is, false otherwise.
 * @name checkXmlSvg
 * @param {string} svgString - The SVG string to check.
 * @returns {Boolean}
 */
export const checkXmlSvg = (svgString) => {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(svgString, 'image/svg+xml')
  const isXmlSvgValid = xmlDoc.getElementsByTagName('parsererror').length === 0

  return isXmlSvgValid
}

/**
 * returns the found APM param object if it exists
 * @name findApmParamByName
 *  @param {object[]} params - an array of param objects
 *  @param {string} name - the identifier to match against
 * @returns {object}
 */
export const findApmParamByName = (params, name) =>
  params?.find?.((el) => el?.featureParam?.name?.toUpperCase() === name?.toUpperCase())

/**
 * Checks if a specific VAS (Value-Added Service) rule exists in the provided list.
 *
 * @param {Array} list - The list of VAS objects for the selected terminal.
 *                        Each object in the list is expected to have a `name` property.
 * @param {string} value - The name of the VAS rule to check for.
 * @returns {boolean} - Returns `true` if the VAS rule exists in the list, otherwise `false`.
 */
export const vasPermissionControl = (list, value) => {
  if (!list || !value) return false
  if (isArray(list) && list.length > 0) {
    return list.findIndex((el) => el.name.toUpperCase() === value.toUpperCase()) > -1
  }
  return false
}

/**
 * Checks if the user profile contains a terminal with the specified subTerminalType.
 *
 * @name checkUserProfile
 * @param {object} userProfile - The UserProfile object.
 * @param {string} val - The subTerminalType value to check for.
 * @returns {boolean} - Returns true if a terminal with the specified subTerminalType exists, otherwise false.
 */

export const checkUserProfile = (userProfile, val) => {
  if (!userProfile || !Array.isArray(userProfile.terminals)) return false
  return userProfile.terminals.some((terminal) => terminal.subTerminalType === val)
}

/**
 * Checks if the subscription array contains a feature with the specified name.
 *
 * @name checkGetSubscription
 * @param {Array} getSubscriptionArray - The GETsubscription objects.
 * @returns {boolean} - Returns true if a feature name with the specified value is found, otherwise false.
 */
export const checkGetSubscription = (getSubscriptionArray, val, terminalId, checkStatus = false) =>
  !!getSubscriptionArray?.some(
    (item) =>
      item.feature?.name?.toUpperCase() === val &&
      (!checkStatus || item.status?.toLowerCase() === 'enabled') &&
      (terminalId ? item.terminalId === terminalId : true)
)

/**
 * Checks if user's browser is set to 12-hour format based on the language and locale.
 *
 * @name check12HourFormat
 * @returns {boolean} - Returns true if user's browser is set to 12-hour format
 */
export const is12HourFormat = () => {
  const language = getLanguage()
  return language === 'en' && navigator.language === 'en-US'
}

/**
 * Costruisce una query RSQL a partire da un array di filtri e opzioni.
 * Ogni filtro è un oggetto: { field, operator, value }
 * Gli operatori supportati sono quelli elencati nell'esempio.
 *
 * @param {Array} filters - Array di filtri, es: [{ field: 'company.code', operator: '==', value: 'demo' }]
 * @param {Object} [options] - Opzioni extra (sort, pageSize, page)
 * @param {'and'|'or'} [logic='and'] - Operatore logico tra i filtri
 * @returns {string} La query RSQL generata
 */
export const buildRSQLQuery = (filters, options = {}, logic = 'and') => {
  const escapeValue = (val) => {
    if (val === null || val === undefined) return
    if (typeof val === 'string') {
      // Se contiene spazi o caratteri speciali, metti tra apici singoli
      if (/[\s,;()'"]/.test(val) || val === '') return `'${val.replace(/'/g, "\\'") || val}'`
      return val
    }
    return val
  }

  const filterStrings = filters.map(({ field, operator, value }) => {
    const mappedOp = RSQL_OPERATOR_MAP[operator] || operator
    if (['in', 'out', 'bt', 'nb', 'ai'].includes(operator)) {
      // Array di valori o range
      if (Array.isArray(value)) {
        return `${field}${mappedOp}(${value.map(escapeValue).join(',')})`
      }
      // Supporta anche stringa già formattata
      return `${field}${mappedOp}(${value})`
    }
    if (['isnull', 'null', 'na', 'nn', 'notnull', 'isnotnull'].includes(operator)) {
      return `${field}${mappedOp}''`
    }
    // Operatori con valore stringa
    return `${field}${mappedOp}${escapeValue(value)}`
  })

  // Unisci i filtri con il separatore corretto
  let filterQuery = ''
  if (logic === 'or') {
    filterQuery = filterStrings.join(',')
  } else {
    filterQuery = filterStrings.join(';')
  }

  // Opzioni extra (sort, pageSize, page)
  const extra = []
  if (options.sort) extra.push(`sort=${options.sort}`)
  if (options.pageSize) extra.push(`pageSize=${options.pageSize}`)
  if (options.page) extra.push(`page=${options.page}`)
  if (options.timeStamp) extra.push(`timeStamp=${options.timeStamp}`)
  if (options.maxRecords) extra.push(`maxRecords=${options.maxRecords}`)

  return `${filterQuery !== '' ? 'search=' : ''}${filterQuery}${filterQuery !== '' && extra.length ? '&' : ''}${
    extra.length ? extra.join('&') : ''
  }`
}

/**
 * Verifica se la response è JSON (header o contenuto)
 * @param {object} response - la response dell'API (axios o fetch)
 * @returns {boolean}
 */
export const isJsonResponse = (response) => {
  // Controllo header
  const contentType =
    response?.headers?.['content-type'] ||
    response?.headers?.get?.('content-type') ||
    response?.headers?.get?.('Content-Type')
  if (typeof contentType === 'string' && contentType.includes('application/json')) {
    return true
  }
  // Controllo contenuto se è stringa
  if (typeof response?.data === 'string') {
    try {
      const parsed = JSON.parse(response.data)
      return typeof parsed === 'object' && parsed !== null
    } catch (e) {
      return false
    }
  }
  // Se è già oggetto, è JSON
  if (typeof response?.data === 'object' && response?.data !== null) {
    return true
  }
  return false
}

export const hasSubscriptionFeature = (subscriptions, featureName, featureParamName, value) => {
  if (!Array.isArray(subscriptions) || !featureName) return false

  return subscriptions.some((subscription) => {
    if (subscription?.feature?.name?.toUpperCase() !== featureName.toUpperCase()) return false
    if (!featureParamName || !value) return true

    return subscription?.params?.some(
      (param) =>
        param?.featureParam?.name?.toUpperCase() === featureParamName.toUpperCase() &&
        param?.value?.toUpperCase() === value.toUpperCase()
    )
  })
}

export const getFeatureParams = (array, featureName, paramName, val, terminal) => {
  if (!terminal) return false

  const filtered = array.filter((item) => {
    if (item?.feature?.name !== featureName) return false
    if (item?.terminalId !== terminal) return false
    if (!paramName) return true
    const paramObj = item?.params?.find(
      (p) =>
        p?.featureParam?.name.toUpperCase() === paramName.toUpperCase() &&
        (val ? p?.value?.toUpperCase() === val.toUpperCase() : true)
    )
    return !!paramObj
  })

  return filtered.length > 0 ? filtered : false
}

export const cleanPathFromTheme = (path) => `${path.toLowerCase().replace(/^\/[^\/]+(?=\/)/, '').replace(/-/g, '')}`

// @generator utility:method
